import { useCallback, useEffect, useState } from "react";

export default function useIndexedDB(
    dbName: string,
    version: number,
    storeName: string,
    storeOptions: IDBObjectStoreParameters,
) {
    const [dbPromise, setDbPromise] = useState<Promise<IDBDatabase> | null>(
        null,
    );

    useEffect(() => {
        setDbPromise(
            new Promise<IDBDatabase>((resolve, reject) => {
                const request = indexedDB.open(dbName, version);
                request.onupgradeneeded = () => {
                    const db = request.result;
                    if (!db.objectStoreNames.contains(storeName)) {
                        db.createObjectStore(storeName, storeOptions);
                    }
                };
                request.onsuccess = () => {
                    const db = request.result;
                    if (!db.objectStoreNames.contains(storeName)) {
                        reject(
                            new Error(
                                `Object store "${storeName}" was not created.`,
                            ),
                        );
                        return;
                    }
                    resolve(db);
                };
                request.onerror = () => {
                    reject(request.error);
                };
            }),
        );

        return () => {
            setDbPromise(null);
        };
    }, [dbName, version, storeName, storeOptions]);

    const get = useCallback(
        async <T>(key: IDBValidKey) => {
            if (!dbPromise) return null;
            const db = await dbPromise;
            const transaction = db.transaction(storeName, "readonly");
            const store = transaction.objectStore(storeName);
            const request = store.get(key);
            return await new Promise<T | undefined>((resolve, reject) => {
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
            });
        },
        [dbPromise, storeName],
    );

    const getAll = useCallback(async <T>() => {
        if (!dbPromise) return null;
        const db = await dbPromise;
        const transaction = db.transaction(storeName, "readonly");
        const store = transaction.objectStore(storeName);
        const request = store.getAll();
        return await new Promise<T[]>((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [dbPromise, storeName]);

    const put = useCallback(
        async <T>(value: T) => {
            if (!dbPromise) return;
            const db = await dbPromise;
            const transaction = db.transaction(storeName, "readwrite");
            const store = transaction.objectStore(storeName);
            const request = store.put(value);
            return await new Promise<void>((resolve, reject) => {
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        },
        [dbPromise, storeName],
    );

    const deleteFile = useCallback(
        async (key: IDBValidKey) => {
            if (!dbPromise) return;
            const db = await dbPromise;
            const transaction = db.transaction(storeName, "readwrite");
            const store = transaction.objectStore(storeName);
            const request = store.delete(key);
            return await new Promise<void>((resolve, reject) => {
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        },
        [dbPromise, storeName],
    );

    return { get, put, getAll, delete: deleteFile };
}
